import type { AgentSessionApiId } from '@/model/Agent.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useMemo } from 'react';
import { sendSessionResult, type SendSessionResult } from '@/components/Agents/SendValidator/utils/SendResults';

interface SendSession {
  /** What the session's executions hold, found by result type; nothing until the session has loaded. */
  sessionResult: SendSessionResult;
  loading: boolean;
  /** The session could not be fetched. Already captured; the caller only says so. */
  failed: boolean;
}

/**
 * Fetches a SEND session and finds its result, for a modal opened from a tree node that supplies only the
 * session id. A failed fetch is captured under the caller's own `slug`, never toasted, so each modal reports
 * it in its own words.
 */
export const useSendSession = (sessionApiId: AgentSessionApiId, slug: string): SendSession => {
  const { response, loading } = useApiHook({
    endpoint: 'GET /api/v1/ai-agents/sessions/{sessionApiId}',
    path: { sessionApiId },
    options: { onError: { throw: false, preserveOriginalError: false, toast: false, capture: true, slug } },
  });
  const sessionResult = useMemo(
    () => sendSessionResult(response?.type === 'success' ? response.body : null),
    [response]
  );
  return { sessionResult, loading, failed: response?.type === 'error' };
};
